import { usePagination } from '@material-ui/lab/Pagination';
import React from 'react';

import Chevron from '@/components/icons/Chevron.svg';

interface IPagination {
  currentPage: number;
  totalPages: number;
  onClick: (newPage: number, limit?: number) => void;
}

const Pagination: React.FC<IPagination> = ({ currentPage, totalPages, onClick }) => {
  const { items } = usePagination({
    count: totalPages,
    page: currentPage,
  });

  if (totalPages <= 1) {
    return <div />;
  }

  return (
    <ul
      data-testid="pagination"
      className="flex items-center justify-center text space-x-2 lg:space-x-1">
      {items.map(({ page, type, selected, ...item }, index) => {
        let children = null;

        if (type === 'start-ellipsis' || type === 'end-ellipsis') {
          children = '…';
        } else if (type === 'page') {
          children = (
            <button
              {...item}
              onClick={ev => {
                item.onClick(ev);
                onClick(page);
              }}
              className={`inline-flex items-center justify-center w-6 h-6 lg:w-8 lg:h-8 text-gray-900 rounded-full hover:bg-gray-100 ${
                selected
                  ? 'bg-green-800 text-white hover:bg-green-900'
                  : 'text-gray-900 hover:bg-gray-100'
              }`}>
              {page}
            </button>
          );
        } else if (type === 'previous') {
          children = previous(page, () => onClick(page));
        } else if (type === 'next') {
          children = next(page, totalPages, () => onClick(page));
        }

        return <li key={index}>{children}</li>;
      })}
    </ul>
  );
};

const previous = (page: number, onClick: () => void) => {
  const isNext = false;
  if (page >= 1) {
    return nextPreviousActiveButton(isNext, onClick);
  } else {
    return nextPreviousDisalbedButton(isNext);
  }
};

const next = (page: number, totalPages: number, onClick: () => void) => {
  const isNext = true;
  if (page <= totalPages) {
    return nextPreviousActiveButton(isNext, onClick);
  } else {
    return nextPreviousDisalbedButton(isNext);
  }
};

const nextPreviousActiveButton = (isNext: boolean, onClick: () => void) => (
  <button
    data-testid={isNext ? 'next-button' : 'prev-button'}
    onClick={onClick}
    className="flex items-center w-6 h-6 lg:w-8 lg:h-8 text-primary-500">
    <span className={`inline-block stroke-current transform ${isNext ? 'rotate-0' : 'rotate-180'}`}>
      <Chevron />
    </span>
  </button>
);

const nextPreviousDisalbedButton = (isNext: boolean) => (
  <span
    data-testid={isNext ? 'next-button' : 'prev-button'}
    className="flex items-center w-6 h-6 text-gray-500 lg:w-8 lg:h-8">
    <span className={`inline-block stroke-current transform ${isNext ? 'rotate-0' : 'rotate-180'}`}>
      <Chevron />
    </span>
  </span>
);

export default Pagination;
